/**
 * dataTripwire — the RUNTIME half of the `data` ban (RES-CLIENT-001 §8.5).
 *
 * §8.5 lists four independent mechanisms that make `.data.data` unwritable. This
 * module is mechanism **3**:
 *
 *   1. **Structural** — no object in application code has a transport `data` layer.
 *   2. **Lexical** — the token `data` appears nowhere in the client contract.
 *   3. **Runtime** — reading `.data` off a result THROWS, naming the right member.
 *   4. **Static** — `web/no-double-response-read` becomes an exemption-free key ban.
 *
 * ## Why it throws
 *
 * The same "a silent wrong answer is worse than a crash" guard as
 * `assertOptionsObject` (`@puffin-core/response-helpers/success.js:39-47`). A
 * wrapper still written against the old contract fails LOUDLY on its first call
 * instead of quietly resolving `undefined` and rendering an empty screen. It adapts
 * nothing and returns nothing; it is a tripwire, not a compatibility shim.
 *
 * ## Why it is NON-ENUMERABLE
 *
 * The tripwire must fire on the MISTAKE and never on legitimate use. An enumerable
 * throwing getter would detonate `{...result}`, `Object.keys`, `JSON.stringify` and
 * any logger that walks own keys — collateral damage that gets a guard deleted the
 * first time it is inconvenient.
 *
 * ⚠️ This is NOT pos-web's `_meta` pattern in disguise. That hid **data** on a
 * non-enumerable property, so a spread silently lost it. This hides **no data at
 * all** — there is nothing a spread can drop.
 *
 * @module @puffin-core/api-client/facade/dataTripwire
 */

/**
 * Define a non-enumerable, non-configurable `data` accessor that always throws.
 *
 * @param {Object} target - The result object under construction.
 * @param {string} message - The TypeError message; must name the correct member.
 * @returns {void}
 */
export function installDataTripwire(target, message) {
  Object.defineProperty(target, 'data', {
    enumerable: false,
    configurable: false,
    get() {
      throw new TypeError(message);
    },
  });
}
